import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { devOnlyFieldSx } from 'src/modules/companies/helpers';
import { BhiDialog } from 'src/UIKit';
import { TintedTextField } from '../TintedTextField';
import GridData from 'src/components/GridData/GridData';
import ApplyForVettingDialog from '../ApplyForVettingDialog';
import { LoadingSpinner, ScrollContainer, StatusChips } from '../shared/components';
import { getCommonFieldProps, getCommonFormControlProps } from '../shared/utils';
import { BrandInfoTabViewModel } from 'src/modules/companies/types';

type BrandInfoTabViewProps = BrandInfoTabViewModel;

const BrandInfoTabView = (props: BrandInfoTabViewProps) => {
  const {
    loading,
    loadingBrandById,
    searchingBrand,
    saving,
    existingBrand,
    brandRelationships,
    entityTypes,
    verticals,
    vettingRows,
    vettingLoading,
    initialDisplayName,
    expandedSections,
    applyForVettingDialogOpen,
    vettingNotAllowedDialogOpen,
    formValues,
    fieldErrors,
    showEinValidationError,
    onInputChange,
    onRegister,
    onSaveInfo,
    isFormValid,
    onAccordionChange,
    onApplyForVetting,
    setApplyForVettingDialogOpen,
    setVettingNotAllowedDialogOpen,
    onSubmitVetting,
  } = props;

  if (loading || loadingBrandById) {
    return <LoadingSpinner />;
  }

  const vettingColumns = [
    {
      field: 'vettingType',
      headerName: 'Vetting Type',
      minWidth: 140,
      sortable: false,
      editable: false,
      flex: 1,
    },
    {
      field: 'vettingPartner',
      headerName: 'Vetting Partner',
      minWidth: 160,
      sortable: false,
      editable: false,
      flex: 1,
    },
    {
      field: 'vettingStatus',
      headerName: 'Vetting Status',
      minWidth: 100,
      sortable: false,
      editable: false,
      flex: 1,
    },
    {
      field: 'requestedDate',
      headerName: 'Requested Date',
      minWidth: 140,
      sortable: false,
      editable: false,
      flex: 1,
    },
    {
      field: 'validUntil',
      headerName: 'Valid Until',
      minWidth: 120,
      sortable: false,
      editable: false,
      flex: 1,
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      minWidth: 120,
      sortable: false,
      editable: false,
      flex: 1,
    },
  ];

  return (
    <Stack mt={2.5} height="calc(100vh - 228px)" overflow="hidden">
      {(searchingBrand || loadingBrandById) && (
        <Typography
          variant="body2"
          color="text.secondary"
          mb={2.5}
          flexShrink={0}
        >
          {loadingBrandById ? 'Loading brand...' : 'Searching for existing brand...'}
        </Typography>
      )}

      <Box pb={2.5} flex={1} overflow="hidden" minHeight={0}>
        <ScrollContainer maxHeight="100%">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TintedTextField
                {...getCommonFieldProps(!!existingBrand)}
                tint={existingBrand ? 'info' : formValues.brandId ? 'success' : 'none'}
                label="Brand ID"
                value={formValues.brandId || ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <StatusChips
                      status={formValues.status}
                      identityStatus={formValues.identityStatus}
                    />
                  ),
                }}
                placeholder={
                  existingBrand
                    ? 'Loaded from existing brand'
                    : 'Will be generated after successful registration'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl {...getCommonFormControlProps(!!existingBrand)} error={!!fieldErrors.entityType}>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  id="entityType"
                  label="Entity Type"
                  value={formValues.entityType}
                  onChange={(e: SelectChangeEvent) => onInputChange('entityType', e.target.value)}
                  disabled={entityTypes.length === 0 || !!existingBrand}
                  fullWidth
                  MenuProps={{ disableScrollLock: true }}
                >
                  {Array.isArray(entityTypes) &&
                    entityTypes.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                </Select>
                {fieldErrors.entityType && (
                  <Typography variant="caption" color="error" mt={1} ml={1.75}>
                    {fieldErrors.entityType}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Accordion
                expanded={expandedSections.basicInfo}
                onChange={() => onAccordionChange('basicInfo')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Basic Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        label="Display Name"
                        value={formValues.displayName}
                        onChange={e => onInputChange('displayName', e.target.value)}
                        error={Boolean(
                          !existingBrand &&
                            formValues.displayName &&
                            initialDisplayName &&
                            formValues.displayName.trim() !== initialDisplayName.trim(),
                        )}
                        helperText={
                          !existingBrand &&
                          formValues.displayName &&
                          initialDisplayName &&
                          formValues.displayName.trim() !== initialDisplayName.trim()
                            ? 'It is strongly recommended to keep the initial value.'
                            : undefined
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="companyName"
                        label="Company Name"
                        value={formValues.companyName}
                        onChange={e => onInputChange('companyName', e.target.value)}
                        error={!!fieldErrors.companyName}
                        helperText={fieldErrors.companyName || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="ein"
                        label="EIN"
                        value={formValues.ein}
                        onChange={e => onInputChange('ein', e.target.value)}
                        error={showEinValidationError || !!fieldErrors.ein}
                        helperText={showEinValidationError ? 'Enter a valid 9-digit EIN in the format XX - XXXXXXX' : fieldErrors.ein || ''}
                        inputProps={{
                          inputMode: 'numeric',
                          maxLength: 12,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="phone"
                        label="Phone"
                        value={formValues.phone}
                        onChange={e => onInputChange('phone', e.target.value)}
                        error={!!fieldErrors.phone}
                        helperText={fieldErrors.phone || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="email"
                        label="Email"
                        value={formValues.email}
                        onChange={e => onInputChange('email', e.target.value)}
                        error={!!fieldErrors.email}
                        helperText={fieldErrors.email || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="website"
                        label="Website"
                        value={formValues.website}
                        onChange={e => onInputChange('website', e.target.value)}
                        error={!!fieldErrors.website}
                        helperText={fieldErrors.website || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl
                        {...getCommonFormControlProps(!!existingBrand)}
                        id="brandRelationship"
                        error={!!fieldErrors.brandRelationship}
                      >
                        <InputLabel>Brand Relationship</InputLabel>
                        <Select
                          label="Brand Relationship"
                          value={formValues.brandRelationship}
                          onChange={(e: SelectChangeEvent) =>
                            onInputChange('brandRelationship', e.target.value)
                          }
                          disabled={brandRelationships.length === 0 || !!existingBrand}
                          fullWidth
                          MenuProps={{ disableScrollLock: true }}
                        >
                          {Array.isArray(brandRelationships) &&
                            brandRelationships.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                        </Select>
                        {fieldErrors.brandRelationship && (
                          <Typography variant="caption" color="error" mt={1} ml={1.75}>
                            {fieldErrors.brandRelationship}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl
                        {...getCommonFormControlProps(!!existingBrand)}
                        id="vertical"
                        error={!!fieldErrors.vertical}
                      >
                        <InputLabel>Vertical</InputLabel>
                        <Select
                          label="Vertical"
                          value={formValues.vertical}
                          onChange={(e: SelectChangeEvent) => onInputChange('vertical', e.target.value)}
                          disabled={verticals.length === 0 || !!existingBrand}
                          fullWidth
                          MenuProps={{ disableScrollLock: true }}
                        >
                          {Array.isArray(verticals) &&
                            verticals.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                        </Select>
                        {fieldErrors.vertical && (
                          <Typography variant="caption" color="error" mt={1} ml={1.75}>
                            {fieldErrors.vertical}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12}>
              <Accordion
                expanded={expandedSections.addressInfo}
                onChange={() => onAccordionChange('addressInfo')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Address Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="street"
                        label="Street Address"
                        value={formValues.street}
                        onChange={e => onInputChange('street', e.target.value)}
                        error={!!fieldErrors.street}
                        helperText={fieldErrors.street || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="city"
                        label="City"
                        value={formValues.city}
                        onChange={e => onInputChange('city', e.target.value)}
                        error={!!fieldErrors.city}
                        helperText={fieldErrors.city || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="state"
                        label="State"
                        value={formValues.state}
                        onChange={e => onInputChange('state', e.target.value)}
                        error={!!fieldErrors.state}
                        helperText={fieldErrors.state || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="postalCode"
                        label="Postal Code"
                        value={formValues.postalCode}
                        onChange={e => onInputChange('postalCode', e.target.value)}
                        error={!!fieldErrors.postalCode}
                        helperText={fieldErrors.postalCode || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="country"
                        label="Country"
                        value={formValues.country}
                        onChange={e => onInputChange('country', e.target.value)}
                        error={!!fieldErrors.country}
                        helperText={fieldErrors.country || ''}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        {...getCommonFieldProps(!!existingBrand)}
                        id="einIssuingCountry"
                        label="EIN Issuing Country"
                        value={formValues.einIssuingCountry}
                        onChange={e => onInputChange('einIssuingCountry', e.target.value)}
                        error={!!fieldErrors.einIssuingCountry}
                        helperText={fieldErrors.einIssuingCountry || ''}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {existingBrand && (
              <Grid item xs={12}>
                <Accordion
                  expanded={expandedSections.vettingDetails}
                  onChange={() => onAccordionChange('vettingDetails')}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      mr={2}
                    >
                      <Typography variant="h6">Vetting Details</Typography>
                      <Fade in={expandedSections.vettingDetails} timeout={300}>
                        <Button
                          variant="contained"
                          onClick={e => {
                            e.stopPropagation();
                            onApplyForVetting();
                          }}
                        >
                          Apply for Vetting
                        </Button>
                      </Fade>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <GridData
                      minHeight={120}
                      isLoading={vettingLoading}
                      rows={vettingRows}
                      columns={vettingColumns}
                      pagination={null}
                      getRowId={row => row.id}
                      noRowsOverlay={() => (
                        <Stack
                          height="100%"
                          alignItems="center"
                          justifyContent="center"
                          textAlign="center"
                        >
                          <Typography variant="body2" color="text.secondary">
                            <strong>No data found,</strong>
                            <br />
                            no vettings to view
                          </Typography>
                        </Stack>
                      )}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}

            <Grid item xs={12} mt={2.5}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.mock}
                    onChange={e => onInputChange('mock', e.target.checked)}
                    color="primary"
                    disabled={!!existingBrand}
                  />
                }
                label="Mock Registration (Development Mode)"
                sx={devOnlyFieldSx}
              />
            </Grid>

            {!existingBrand && (
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3.75}>
                  <Button
                    variant="outlined"
                    onClick={onSaveInfo}
                    disabled={saving || !isFormValid()}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                  >
                    {saving ? 'Saving...' : 'Save Info'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={onRegister}
                    disabled={
                      !isFormValid() ||
                      saving ||
                      entityTypes.length === 0 ||
                      brandRelationships.length === 0 ||
                      verticals.length === 0
                    }
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                  >
                    {saving ? 'Registering...' : 'Register Brand'}
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
        </ScrollContainer>
      </Box>

      <ApplyForVettingDialog
        open={applyForVettingDialogOpen}
        onClose={() => setApplyForVettingDialogOpen(false)}
        onSubmit={onSubmitVetting}
      />

      <BhiDialog
        isOpen={vettingNotAllowedDialogOpen}
        onClose={() => setVettingNotAllowedDialogOpen(false)}
        title="Vetting Not Available"
        maxWidth="sm"
      >
        <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
          <Typography variant="body1">
            A brand with Sole Proprietor entity type cannot request external vetting.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setVettingNotAllowedDialogOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </BhiDialog>
    </Stack>
  );
};

export default BrandInfoTabView;
